import {
  Button,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useCallback, useRef, useState } from 'react';
import { useAuth, useCities, useGuest } from '@/logic/state';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useUpdateUserSelectedCity } from '@/logic/hooks';
import ImageBoxFuture from '../shared/image-box-future';

const ArrowDownIcon = '/icons/shared/arrow-down.svg';
const LocationIcon = '/icons/navbar/location.svg';

const CitiesMenu: FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const anchorRef = useRef(null);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('mediumDesktop'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('extraLargeDesktop'));
  const [isOpen, setIsOpen] = useState(false);

  const cities = useCities(s => s.cities, shallow);
  const guest = useGuest(s => s.guest, shallow);
  const user = useAuth(s => s.user, shallow);

  const updateUserSelectedCity = useUpdateUserSelectedCity(() => setIsOpen(false));

  const getSelectedCityName = useCallback(
    (selectedCityId: number) => {
      return (
        cities.citiesList.find(city => city.id === selectedCityId)?.name ??
        intl.formatMessage({
          id: 'global.city',
        })
      );
    },
    [cities.citiesList, intl]
  );

  return (
    <>
      <Button
        fullWidth={false}
        ref={anchorRef}
        sx={{
          display: {
            mobile: 'none',
            mediumDesktop: 'flex',
          },
        }}
        onClick={() => (!isOpen ? setIsOpen(true) : null)}
      >
        <Stack direction='row' alignItems='center' spacing={0.5}>
          {updateUserSelectedCity.isLoading ? (
            <CustomizedCircularProgress
              size={isMediumScreen ? 15 : 24}
              customSx={{ marginBottom: '-5px' }}
            />
          ) : (
            <ImageBoxFuture
              img={LocationIcon}
              title={intl.formatMessage({
                id: 'images.title.location',
              })}
              objectFit='contain'
              sx={{
                width: '15px',
                height: '18px',
              }}
            />
          )}
          <Typography
            component='span'
            sx={{
              fontFamily: 'co-head-line-corp-bold',
              fontSize: pxToRem(20),
              color: theme.palette.primary.main,
            }}
          >
            {user?.preferredCityId ? (
              getSelectedCityName(user.preferredCityId)
            ) : guest?.preferredCityId ? (
              getSelectedCityName(guest.preferredCityId)
            ) : (
              <FormattedMessage id='global.city' />
            )}
          </Typography>

          <ImageBoxFuture
            img={ArrowDownIcon}
            title={intl.formatMessage({
              id: 'alt.arrow_down_icon',
            })}
            objectFit='contain'
            sx={{
              marginLeft: '8px !important',
              transform: isOpen ? 'scaleY(-1)' : 'none',
              width: '24px',
              height: '24px',
            }}
          />
        </Stack>
      </Button>
      <Popper
        open={isMobileScreen ? false : isOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        transition
        disablePortal
        sx={{
          top: `${pxToRem(20)} !important`,
          minWidth: pxToRem(235),
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper
              sx={{
                borderRadius: pxToRem(12),
                boxShadow:
                  '0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0.893452px 17.869px rgba(0, 0, 0, 0.160949);', //TODO: Move to palette
              }}
            >
              <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <RadioGroup
                  value={
                    user ? user.preferredCityId : guest ? guest.preferredCityId : null
                  }
                  onChange={updateUserSelectedCity.update}
                  sx={{
                    paddingY: pxToRem(26.5),
                    paddingX: pxToRem(16),
                  }}
                >
                  {cities.citiesList.map(({ id, name }) => (
                    <FormControlLabel
                      key={id}
                      value={id}
                      control={<Radio />}
                      label={name}
                      sx={{
                        margin: '0px',
                        marginBottom: pxToRem(21),
                        '& .MuiRadio-root': {
                          padding: '0px',
                          marginRight: pxToRem(16),
                        },
                        '& .MuiFormControlLabel-label': {
                          fontSize: pxToRem(18),
                          letterSpacing: pxToRem(0.1),
                          color: theme.palette.grey[80],
                        },
                        '&:last-of-type': {
                          marginBottom: '0px',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default CitiesMenu;
